import { useEffect, useState } from 'react';
import { useSellerAdsAgentConfig, type AdsAgentConfig } from '../../hooks/useSellerAdsAgent';
import { Advanced, RuleField, SaveStatus, SettingsSection, useAutoSave } from './settingsUi';

// Aba "Metas e regras de Ads": o que o agente de campanhas considera bom/ruim e o
// que ele pode fazer sozinho. As rotinas (ligar/frequencia) moram em "Rotinas de Ads".
// Antes eram ~15 numeros soltos em 7 blocos; agora 3 metas a vista e o resto em Avancado.

const DEFAULTS: AdsAgentConfig = {
  executionMode: 'manual',
  minDaysBetweenActions: 7,
  autoSyncMetrics: false,
  syncIntervalHours: 24,
  targetAcos: 25,
  criticalAcos: 75,
  tacosMax: 12,
  launchAcosMax: 50,
  launchDays: 30,
  learningDaysShopee: 7,
  learningDaysMl: 14,
  minClicksToJudge: 20,
  budgetScaleStepPercent: 30,
  lowStockThreshold: 5,
  autoGuardrail: false,
  guardrailIntervalHours: 24,
  autoPortfolio: false,
  portfolioIntervalHours: 168,
  businessContext: null,
};

const MODES: Array<{ value: AdsAgentConfig['executionMode']; label: string; help: string }> = [
  { value: 'recommend', label: 'Só sugerir', help: 'A IA explica o que faria, mas não aparece botão de executar.' },
  { value: 'manual', label: 'Sugerir e eu aprovo (recomendado)', help: 'Cada sugestão vem com um botão "Executar". Nada muda sem o seu clique.' },
  { value: 'auto', label: 'Executar sozinha', help: 'A IA aplica no máximo 1 ação por campanha a cada rodada, respeitando as regras abaixo.' },
];

export function SellerAdsAgentSettingsView() {
  const { config, loading, saveConfig } = useSellerAdsAgentConfig();
  const [form, setForm] = useState<AdsAgentConfig | null>(null);
  const { status, schedule } = useAutoSave<AdsAgentConfig>(saveConfig);

  useEffect(() => {
    if (config && !form) setForm({ ...DEFAULTS, ...config });
  }, [config, form]);

  if (!form) return <p className="text-sm text-gray-500">{loading ? 'Carregando...' : 'Não foi possível carregar.'}</p>;

  const set = <K extends keyof AdsAgentConfig>(key: K, value: AdsAgentConfig[K], immediate = false) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    schedule({ [key]: value } as Partial<AdsAgentConfig>, immediate);
  };

  const roas = form.targetAcos > 0 ? (100 / form.targetAcos).toFixed(1).replace('.', ',') : '—';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Valem para as campanhas da Shopee Ads e do Mercado Ads.</p>
        <SaveStatus status={status} />
      </div>

      <SettingsSection title="O que a IA pode fazer">
        <div className="space-y-2">
          {MODES.map((opt) => (
            <label key={opt.value} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 ${form.executionMode === opt.value ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200'}`}>
              <input
                type="radio"
                name="executionMode"
                checked={form.executionMode === opt.value}
                onChange={() => set('executionMode', opt.value, true)}
                className="mt-0.5 h-4 w-4 border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span>
                <span className="block text-xs font-medium text-gray-800">{opt.label}</span>
                <span className="block text-[11px] text-gray-500">{opt.help}</span>
              </span>
            </label>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Quanto você aceita gastar" help="É assim que a IA decide se uma campanha está boa, em atenção ou crítica.">
        <RuleField
          label="Meta: gasto em Ads para cada R$ 100 vendidos"
          tech="ACOS alvo"
          suffix="%"
          value={form.targetAcos}
          defaultValue={DEFAULTS.targetAcos}
          onChange={(v) => set('targetAcos', v ?? 0)}
          help={`Ex.: ${form.targetAcos}% = até R$ ${form.targetAcos} de Ads para cada R$ 100 (o mesmo que ROAS ${roas}x). Acima disso a campanha entra em atenção.`}
        />
        <RuleField
          label="Limite: acima disso a campanha está queimando dinheiro"
          tech="ACOS crítico"
          suffix="%"
          value={form.criticalAcos}
          defaultValue={DEFAULTS.criticalAcos}
          onChange={(v) => set('criticalAcos', v ?? 0)}
          help="A campanha vira crítica e vai pro topo de 'Para revisar'."
        />
        <RuleField
          label="Gasto total em Ads sobre o faturamento da conta inteira"
          tech="TACOS"
          suffix="%"
          value={form.tacosMax}
          defaultValue={DEFAULTS.tacosMax}
          onChange={(v) => set('tacosMax', v ?? 0)}
          help="Todo o Ads dividido por tudo que a conta vendeu (orgânico + pago)."
        />
      </SettingsSection>

      <SettingsSection title="Contexto do seu negócio" help="Opcional. A IA lê este texto em toda análise.">
        <textarea
          value={form.businessContext || ''}
          onChange={(e) => set('businessContext', e.target.value || null)}
          rows={3}
          placeholder='Ex.: "margem apertada em eletrônicos, priorizar lucro"; "girar o estoque da linha X até março".'
          className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
        />
      </SettingsSection>

      <Advanced help="Proteções e ajustes finos. Os padrões funcionam bem para a maioria das contas.">
        <div>
          <p className="mb-2 text-xs font-semibold text-gray-700">Campanha nova (lançamento)</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <RuleField label="Gasto aceito enquanto é nova" tech="ACOS" suffix="%" value={form.launchAcosMax} defaultValue={DEFAULTS.launchAcosMax} onChange={(v) => set('launchAcosMax', v ?? 0)} help="Campanha nova pode sair mais cara enquanto ganha histórico." />
            <RuleField label="Até quantos dias ela conta como nova" suffix="dias" integer value={form.launchDays} defaultValue={DEFAULTS.launchDays} onChange={(v) => set('launchDays', v ?? 0)} />
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold text-gray-700">Não mexer cedo demais</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <RuleField label="Shopee: dias sem mexer após criar" suffix="dias" integer value={form.learningDaysShopee} defaultValue={DEFAULTS.learningDaysShopee} onChange={(v) => set('learningDaysShopee', v ?? 0)} help="A Shopee recomenda não editar GMV Max na primeira semana." />
            <RuleField label="Mercado Livre: dias sem mexer após criar" suffix="dias" integer value={form.learningDaysMl} defaultValue={DEFAULTS.learningDaysMl} onChange={(v) => set('learningDaysMl', v ?? 0)} />
            <RuleField label="Cliques mínimos antes de julgar" integer value={form.minClicksToJudge} defaultValue={DEFAULTS.minClicksToJudge} onChange={(v) => set('minClicksToJudge', v ?? 0)} help="Com menos cliques, a IA não conclui nada sobre conversão." />
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold text-gray-700">Ações</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <RuleField label="Quanto aumentar o orçamento ao escalar" suffix="%" value={form.budgetScaleStepPercent} defaultValue={DEFAULTS.budgetScaleStepPercent} onChange={(v) => set('budgetScaleStepPercent', v ?? 0)} help="Aumento por vez quando a campanha está vendendo acima da meta." />
            <RuleField label="Não repetir a mesma ação na campanha antes de" suffix="dias" integer value={form.minDaysBetweenActions} defaultValue={DEFAULTS.minDaysBetweenActions} onChange={(v) => set('minDaysBetweenActions', v ?? 0)} />
            <RuleField label="Avisar quando o estoque do produto anunciado ficar abaixo de" suffix="un." integer value={form.lowStockThreshold} defaultValue={DEFAULTS.lowStockThreshold} onChange={(v) => set('lowStockThreshold', v ?? 0)} />
          </div>
        </div>
      </Advanced>
    </div>
  );
}
